import * as React from "react";
import { homeScrollLayout } from "./scrollLayout";

// Cold-load retry cadence: the overlay measurement lands asynchronously;
// when it does, the measured `pages` remounts drei's scroll container and
// ScrollLayoutMeasurer restores the pre-remount scrollTop right after — a
// one-shot scroll issued in between gets overwritten. So on cold load the
// position is re-applied every tick until it actually sticks.
const RETRY_MS = 120;
// Generous window: on a cold prod load the overlay sections (and their
// anchors) exist only once the CMS content query resolves, which can take
// well over 10s on slow connections — email deep links must survive that.
const MAX_TRIES = 250; // ~30s, then give up quietly

type Resolution =
  | { kind: "done" } // nothing to scroll (no hash / route hash / degenerate layout)
  | { kind: "retry" } // layout or container not ready yet
  | { kind: "top"; top: number };

export function useScrollToHash(scrollEl: HTMLElement | null) {
  React.useEffect(() => {
    if (!scrollEl) return;

    const anchorKeyFromHash = (hash: string): string | null => {
      // Ignore HashRouter route paths (e.g. "#/decks")
      if (!hash || hash.startsWith("#/")) return null;
      return decodeURIComponent(hash.slice(1));
    };

    const resolve = (hash: string): Resolution => {
      const key = anchorKeyFromHash(hash);
      if (!key) return { kind: "done" };
      const m = homeScrollLayout.get();
      if (m == null || m.vh <= 0) return { kind: "retry" };
      // Any measured [data-scroll-anchor] key works (#services, #decks,
      // #service-live-tarot, …); unknown hashes (#home) keep the legacy
      // scroll-to-top behaviour.
      const anchorPx = m.anchors[key] ?? 0;
      let top = 0;
      if (anchorPx > 0) {
        // drei translates the overlay by (pages−1)·vh·offset while
        // maxScroll = pages·vh ⟹ scrollTop = anchorPx · pages/(pages−1).
        const pages = m.contentHeight / m.vh;
        if (pages <= 1) return { kind: "done" };
        top = (anchorPx * pages) / (pages - 1);
      }
      if (scrollEl.scrollHeight - scrollEl.clientHeight < top - 4) {
        return { kind: "retry" };
      }
      return { kind: "top", top };
    };

    // In-app hash navigation (footer links): the layout is long settled, so a
    // one-shot smooth scroll matches the SideNav behaviour.
    const onHashChange = () => {
      const r = resolve(window.location.hash);
      if (r.kind === "top") scrollEl.scrollTo({ top: r.top, behavior: "smooth" });
    };
    window.addEventListener("hashchange", onHashChange);

    // Cold load: jump instantly and keep re-applying until the position
    // survives drei's remount/restore churn.
    let tries = 0;
    let timer = 0;
    const tick = () => {
      const r = resolve(window.location.hash);
      if (r.kind === "done") {
        window.clearInterval(timer);
        return;
      }
      if (r.kind === "top") {
        if (Math.abs(scrollEl.scrollTop - r.top) <= 4) {
          window.clearInterval(timer);
          return;
        }
        scrollEl.scrollTo({ top: r.top, behavior: "auto" });
      }
      if (++tries >= MAX_TRIES) window.clearInterval(timer);
    };
    timer = window.setInterval(tick, RETRY_MS);
    tick();

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [scrollEl]);
}
